import { z } from 'zod';

export const objectStatuses = ['planned', 'active', 'suspended', 'completed'] as const;
export type ObjectStatus = (typeof objectStatuses)[number];
export const workStatuses = ['planned', 'in_progress', 'done', 'cancelled'] as const;
/** What is done on the object; the costing of its works depends on it. */
export const objectWorkTypes = ['construction', 'transportation'] as const;
export type ObjectWorkType = (typeof objectWorkTypes)[number];
export type WorkStatus = (typeof workStatuses)[number];

/** ISO date ("2026-10-25"), or empty when not set. */
const optionalDate = z.union([z.literal(''), z.iso.date('Укажите дату')]);

const period = <T extends { startsOn: string; endsOn: string }>(value: T) =>
  !value.startsOn || !value.endsOn || value.endsOn >= value.startsOn;
const periodError = { message: 'Окончание не раньше начала', path: ['endsOn'] };

export const constructionObjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string(),
  customer: z.string(),
  description: z.string(),
  status: z.enum(objectStatuses),
  workType: z.enum(objectWorkTypes),
  startsOn: z.string(),
  endsOn: z.string(),
  managerUserId: z.number().int().nullable(),
  workCount: z.number().int(),
  version: z.number().int(),
});
export type ConstructionObject = z.infer<typeof constructionObjectSchema>;

const objectFields = z.object({
  name: z.string().trim().min(1, 'Введите название').max(200, 'Не длиннее 200 символов'),
  address: z.string().trim().max(500, 'Не длиннее 500 символов').default(''),
  customer: z.string().trim().max(300, 'Не длиннее 300 символов').default(''),
  description: z.string().trim().max(2000, 'Не длиннее 2000 символов').default(''),
  status: z.enum(objectStatuses).default('planned'),
  workType: z.enum(objectWorkTypes).default('construction'),
  startsOn: optionalDate.default(''),
  endsOn: optionalDate.default(''),
  managerUserId: z.number().int().positive().nullable().default(null),
});

export const createObjectSchema = objectFields.refine(period, periodError);
export type CreateObject = z.infer<typeof createObjectSchema>;

/** The whole card is sent; ``version`` guards against overwriting someone's change. */
export const updateObjectSchema = objectFields
  .extend({ version: z.number().int().positive() })
  .refine(period, periodError);
export type UpdateObject = z.infer<typeof updateObjectSchema>;

export const objectFilterSchema = z.object({ status: z.enum(objectStatuses).optional() });

export const workSchema = z.object({
  id: z.string(),
  objectId: z.string(),
  name: z.string(),
  description: z.string(),
  status: z.enum(workStatuses),
  startsOn: z.string(),
  endsOn: z.string(),
  assigneeUserIds: z.array(z.number().int()),
  version: z.number().int(),
});
export type Work = z.infer<typeof workSchema>;

const workFields = z.object({
  name: z.string().trim().min(1, 'Введите название').max(200, 'Не длиннее 200 символов'),
  description: z.string().trim().max(2000, 'Не длиннее 2000 символов').default(''),
  status: z.enum(workStatuses).default('planned'),
  startsOn: optionalDate.default(''),
  endsOn: optionalDate.default(''),
  assigneeUserIds: z.array(z.number().int().positive()).max(50).default([]),
});

export const createWorkSchema = workFields.refine(period, periodError);
export type CreateWork = z.infer<typeof createWorkSchema>;

export const updateWorkSchema = workFields
  .extend({ version: z.number().int().positive() })
  .refine(period, periodError);
export type UpdateWork = z.infer<typeof updateWorkSchema>;

export const workFilterSchema = z.object({
  objectId: z.uuid().optional(),
  status: z.enum(workStatuses).optional(),
  assigneeUserId: z.coerce.number().int().positive().optional(),
});

export const objectParamsSchema = z.object({
  organizationId: z.coerce.number().int().positive(),
  objectId: z.uuid(),
});
export const workParamsSchema = z.object({
  organizationId: z.coerce.number().int().positive(),
  workId: z.uuid(),
});
